import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../App";
import { AppLayout } from "./DashboardPage";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  ArrowLeftRight,
  Check,
  X,
  AlertCircle,
  ArrowRight,
  Unlink,
  RefreshCw,
  HelpCircle
} from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

export function TransfersContent() {
  const { household } = useAuth();
  const [detectedTransfers, setDetectedTransfers] = useState([]);
  const [confirmedTransfers, setConfirmedTransfers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [activeTab, setActiveTab] = useState("detected");

  const fetchTransfers = useCallback(async () => {
    if (!household?.household_id) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    try {
      const [detectedRes, confirmedRes] = await Promise.all([
        api.transfers.detected(household.household_id),
        api.transfers.confirmed(household.household_id)
      ]);
      
      if (detectedRes.ok) {
        const data = await detectedRes.json();
        setDetectedTransfers(data.transfers || []);
      }
      
      if (confirmedRes.ok) {
        const data = await confirmedRes.json();
        setConfirmedTransfers(data.transfers || []);
      }
    } catch (error) {
      console.error("Error fetching transfers:", error);
      toast.error("Could not load transfers. Please refresh the page.");
    } finally {
      setLoading(false);
    }
  }, [household?.household_id]);

  useEffect(() => {
    fetchTransfers();
  }, [fetchTransfers]);

  const confirmTransfer = async (fromId, toId) => {
    setProcessing(`${fromId}-${toId}`);
    try {
      const res = await api.transfers.confirm(household.household_id, fromId, toId);
      
      if (res.ok) {
        toast.success("Transfer confirmed");
        fetchTransfers();
      } else {
        toast.error("Could not confirm transfer. Please try again.");
      }
    } catch (error) {
      console.error("Confirm transfer error:", error);
      toast.error("Could not confirm transfer. Please try again.");
    } finally {
      setProcessing(null);
    }
  };

  const dismissTransfer = async (fromId, toId) => {
    setProcessing(`${fromId}-${toId}`);
    try {
      const res = await api.transfers.dismiss(household.household_id, [fromId, toId]);
      
      if (res.ok) {
        toast.success("Not a transfer - noted");
        setDetectedTransfers(prev => 
          prev.filter(t => 
            t.from_txn.transaction_id !== fromId || t.to_txn.transaction_id !== toId
          )
        );
      } else {
        toast.error("Could not dismiss transfer. Please try again.");
      }
    } catch (error) {
      console.error("Dismiss transfer error:", error);
      toast.error("Could not dismiss transfer. Please try again.");
    } finally {
      setProcessing(null);
    }
  };

  const unlinkTransfer = async (groupId) => {
    setProcessing(groupId);
    try {
      const res = await api.transfers.unlink(household.household_id, groupId);
      
      if (res.ok) {
        toast.success("Transfer unlinked");
        fetchTransfers();
      } else {
        toast.error("Could not unlink transfer. Please try again.");
      }
    } catch (error) {
      console.error("Unlink transfer error:", error);
      toast.error("Could not unlink transfer. Please try again.");
    } finally {
      setProcessing(null);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-NZ", {
      style: "currency",
      currency: household?.currency || "NZD"
    }).format(Math.abs(amount));
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("en-NZ", {
      day: "numeric",
      month: "short"
    });
  };

  const getConfidenceBadge = (confidence) => {
    if (confidence >= 0.8) {
      return <Badge className="bg-green-500">High</Badge>;
    } else if (confidence >= 0.6) {
      return <Badge className="bg-yellow-500">Medium</Badge>;
    } else {
      return <Badge className="bg-orange-500">Low</Badge>;
    }
  };

  return (
    <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <ArrowLeftRight className="h-6 w-6 text-teal-600" />
              Transfer Detection
            </h1>
            <p className="text-gray-500 mt-1">
              Review and confirm transfers between your accounts
            </p>
          </div>
          <Button 
            variant="outline" 
            onClick={fetchTransfers}
            disabled={loading}
            data-testid="refresh-transfers-btn"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>

        {/* Info Card */}
        <Card className="bg-blue-50 border-blue-200 dark:bg-blue-950/30 dark:border-blue-900">
          <CardContent className="p-4">
            <div className="flex gap-3">
              <HelpCircle className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800 dark:text-blue-200">
                <p className="font-medium">Why confirm transfers?</p>
                <p className="mt-1">
                  Transfers between your own accounts shouldn't count as income or expenses. 
                  Confirming transfers ensures your reports show accurate spending and income totals.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="detected" data-testid="detected-tab">
              Detected ({detectedTransfers.length})
            </TabsTrigger>
            <TabsTrigger value="confirmed" data-testid="confirmed-tab">
              Confirmed ({confirmedTransfers.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="detected" className="mt-4">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
              </div>
            ) : detectedTransfers.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Check className="h-12 w-12 mx-auto text-green-500 mb-3" />
                  <h3 className="font-medium text-lg">All caught up!</h3>
                  <p className="text-gray-500 mt-1">
                    No potential transfers detected. Transfers are automatically detected when you import transactions.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {detectedTransfers.map((transfer, idx) => (
                  <TransferCard
                    key={idx}
                    transfer={transfer}
                    onConfirm={() => confirmTransfer(
                      transfer.from_txn.transaction_id,
                      transfer.to_txn.transaction_id
                    )}
                    onDismiss={() => dismissTransfer(
                      transfer.from_txn.transaction_id,
                      transfer.to_txn.transaction_id
                    )}
                    processing={processing === `${transfer.from_txn.transaction_id}-${transfer.to_txn.transaction_id}`}
                    formatCurrency={formatCurrency}
                    formatDate={formatDate}
                    getConfidenceBadge={getConfidenceBadge}
                  />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="confirmed" className="mt-4">
            {confirmedTransfers.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <ArrowLeftRight className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                  <h3 className="font-medium text-lg">No confirmed transfers</h3>
                  <p className="text-gray-500 mt-1">
                    Confirmed transfers will appear here
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {confirmedTransfers.map((group, idx) => (
                  <ConfirmedTransferCard
                    key={idx}
                    group={group}
                    onUnlink={() => unlinkTransfer(group.transfer_group_id)}
                    processing={processing === group.transfer_group_id}
                    formatCurrency={formatCurrency}
                    formatDate={formatDate}
                  />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
  );
}

export default function TransfersPage() {
  return <AppLayout><TransfersContent /></AppLayout>;
}

function TransferCard({ 
  transfer, 
  onConfirm, 
  onDismiss, 
  processing, 
  formatCurrency, 
  formatDate,
  getConfidenceBadge 
}) {
  const { from_txn, to_txn, confidence, days_apart, amount_diff } = transfer;
  
  return (
    <Card data-testid="transfer-card">
      <CardContent className="p-4">
        <div className="flex flex-col lg:flex-row lg:items-center gap-4">
          {/* From Transaction */}
          <div className="flex-1 bg-red-50 dark:bg-red-950/30 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-2 h-2 rounded-full bg-red-500" />
              <span className="text-xs text-gray-500 dark:text-gray-400 uppercase">From</span>
              <span className="text-sm font-medium">{from_txn.account_name}</span>
            </div>
            <p className="font-medium truncate">{from_txn.description}</p>
            <div className="flex justify-between items-center mt-1">
              <span className="text-sm text-gray-500 dark:text-gray-400">{formatDate(from_txn.date)}</span>
              <span className="font-bold text-red-600 dark:text-red-400">-{formatCurrency(from_txn.amount)}</span>
            </div>
          </div>

          {/* Arrow */}
          <div className="flex items-center justify-center lg:flex-col gap-2">
            <ArrowRight className="h-5 w-5 text-gray-400 hidden lg:block" />
            <div className="lg:hidden w-full border-t border-dashed border-gray-300 dark:border-gray-700" />
            <div className="flex items-center gap-2">
              {getConfidenceBadge(confidence)}
              {days_apart > 0 && (
                <span className="text-xs text-gray-500">{days_apart}d apart</span>
              )}
            </div>
          </div>

          {/* To Transaction */}
          <div className="flex-1 bg-green-50 dark:bg-green-950/30 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-2 h-2 rounded-full bg-green-500" />
              <span className="text-xs text-gray-500 dark:text-gray-400 uppercase">To</span>
              <span className="text-sm font-medium">{to_txn.account_name}</span>
            </div>
            <p className="font-medium truncate">{to_txn.description}</p>
            <div className="flex justify-between items-center mt-1">
              <span className="text-sm text-gray-500 dark:text-gray-400">{formatDate(to_txn.date)}</span>
              <span className="font-bold text-green-600 dark:text-green-400">+{formatCurrency(to_txn.amount)}</span>
            </div>
          </div>

          {/* Actions */}
          <div className="flex lg:flex-col gap-2">
            <Button
              onClick={onConfirm}
              disabled={processing}
              className="flex-1 bg-teal-600 hover:bg-teal-700"
              data-testid="confirm-transfer-btn"
            >
              <Check className="h-4 w-4 mr-1" />
              Confirm
            </Button>
            <Button
              variant="outline"
              onClick={onDismiss}
              disabled={processing}
              className="flex-1"
              data-testid="dismiss-transfer-btn"
            >
              <X className="h-4 w-4 mr-1" />
              Not a transfer
            </Button>
          </div>
        </div>

        {amount_diff > 0.01 && (
          <div className="mt-3 flex items-center gap-2 text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30 px-3 py-2 rounded">
            <AlertCircle className="h-4 w-4" />
            Amount difference: {formatCurrency(amount_diff)} (may include fees)
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ConfirmedTransferCard({ group, onUnlink, processing, formatCurrency, formatDate }) {
  const transactions = group.transactions || [];
  const fromTxn = transactions.find(t => t.transfer_direction === "out") || transactions[0];
  const toTxn = transactions.find(t => t.transfer_direction === "in") || transactions[1];
  
  if (!fromTxn || !toTxn) return null;
  
  return (
    <Card data-testid="confirmed-transfer-card">
      <CardContent className="p-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <span className="font-medium">{fromTxn.account_name}</span>
                <ArrowRight className="h-4 w-4 text-gray-400" />
                <span className="font-medium">{toTxn.account_name}</span>
              </div>
              <Badge variant="secondary">
                <ArrowLeftRight className="h-3 w-3 mr-1" />
                Transfer
              </Badge>
            </div>
            <div className="flex items-center gap-4 mt-2 text-sm text-gray-500 dark:text-gray-400">
              <span>{formatDate(fromTxn.date)}</span>
              <span className="font-medium text-gray-900 dark:text-gray-100">{formatCurrency(group.amount)}</span>
              <span className="truncate max-w-[200px]">{fromTxn.description}</span>
            </div>
          </div>
          
          <Button
            variant="ghost"
            size="sm"
            onClick={onUnlink}
            disabled={processing}
            className="text-gray-500 hover:text-red-600"
            data-testid="unlink-transfer-btn"
          >
            <Unlink className="h-4 w-4 mr-1" />
            Unlink
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
